import { api } from '@/lib/api';

// Types
// LEGITIMATE: Prisma `Location.active: Boolean` only tracks active/inactive writes.
export type PersistedLocationStatus = 'active' | 'inactive';
// LEGITIMATE: Prisma `Location.active: Boolean` only tracks 2 persisted states.
// `maintenance` is read-only legacy/presentational data and must never be sent by write flows.
export type LocationStatus = PersistedLocationStatus | 'maintenance';

export interface Location {
  id: string;
  code: string;
  name: string;
  address?: string | null;
  neighborhood?: string;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  email?: string | null;
  status: LocationStatus;
  // Story 69.8: o que está sem unidade conta nesta.
  isDefault?: boolean;
  openingHours?: Record<string, { open: string; close: string }>;
  createdAt: string;
  updatedAt: string;
  _count?: {
    professionals: number;
    patients: number;
    appointments?: number;
  };
}

export interface CreateLocationDto {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  status?: PersistedLocationStatus;
  openingHours?: Record<string, { open: string; close: string }>;
}

// Story 70.41 [RC-220]: na unidade que existe, `null` apaga o campo; ausente mantém o que está gravado.
export const CLEARABLE_LOCATION_FIELDS = ['address', 'city', 'state', 'zipCode', 'phone', 'email'] as const;
export type ClearableLocationField = (typeof CLEARABLE_LOCATION_FIELDS)[number];

export type UpdateLocationDto = Partial<Omit<CreateLocationDto, ClearableLocationField>> & {
  [K in ClearableLocationField]?: string | null;
};

type LocationText = 'name' | ClearableLocationField;

/**
 * Story 70.41 (RC-132, RC-143): a regra única do que mandar ao editar uma unidade que existe,
 * usada pela tela Unidades e pelo quiz. Igual ao gravado não vai; texto novo vai com trim; campo
 * esvaziado que tinha valor vai `null`. O nome nunca vai vazio.
 */
export function locationUpdateBody(
  current: Partial<Pick<Location, LocationText>>,
  wanted: Partial<Record<LocationText, string>>,
): UpdateLocationDto {
  const body: UpdateLocationDto = {};
  for (const field of Object.keys(wanted) as LocationText[]) {
    const next = (wanted[field] ?? '').trim();
    if (next === (current[field] ?? '').trim()) continue;
    if (next !== '') body[field] = next;
    else if (field !== 'name') body[field] = null;
  }
  return body;
}

export interface LocationsResponse {
  locations: Location[];
  total: number;
}

export interface LocationStats {
  totalPatients: number;
  totalProfessionals: number;
  totalAppointments: number;
  totalRevenue: number;
  attendedDays: number;
}

export interface LocationComparison {
  locationId: string;
  locationName: string;
  revenue: number;
  appointments: number;
  newPatients: number;
  avgTicket: number;
}

export interface QueryLocationsParams {
  active?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

// API Functions
export async function getLocations(params?: QueryLocationsParams): Promise<LocationsResponse> {
  const { data } = await api.get<LocationsResponse>('/locations', { params });
  return data;
}

export async function getLocation(id: string): Promise<Location> {
  const { data } = await api.get<Location>(`/locations/${id}`);
  return data;
}

export async function createLocation(dto: CreateLocationDto): Promise<Location> {
  const { data } = await api.post<Location>('/locations', dto);
  return data;
}

export async function updateLocation(id: string, dto: UpdateLocationDto): Promise<Location> {
  const { data } = await api.put<Location>(`/locations/${id}`, dto);
  return data;
}

export async function deleteLocation(id: string): Promise<void> {
  await api.delete(`/locations/${id}`);
}

export async function setDefaultLocation(id: string): Promise<Location> {
  const { data } = await api.post<Location>(`/locations/${id}/default`);
  return data;
}

// Story 69.8: agendamentos sem unidade, para o banner e a página "Organizar unidades".
export interface UnassignedSummary {
  activeCount: number;
  defaultLocation: { id: string; name: string; city?: string | null; state?: string | null } | null;
  appointmentsWithoutUnit: number;
}

export interface UnassignedFilter {
  from?: string;
  to?: string;
  weekday?: number;
  professionalId?: string;
}

export interface UnassignedAppointment {
  id: string;
  startTime: string;
  status: string;
  patientName: string | null;
  professional: { id: string; name: string } | null;
  serviceName: string | null;
  receivedAmount: number;
}

export async function getUnassignedSummary(): Promise<UnassignedSummary> {
  const { data } = await api.get<UnassignedSummary>('/locations/unassigned/summary');
  return data;
}

export async function getUnassignedAppointments(
  params: UnassignedFilter & { limit?: number; offset?: number },
): Promise<{ total: number; items: UnassignedAppointment[] }> {
  const { data } = await api.get<{ total: number; items: UnassignedAppointment[] }>(
    '/locations/unassigned/appointments',
    { params },
  );
  return data;
}

export async function assignUnassignedAppointments(
  locationId: string,
  input: { appointmentIds?: string[]; filter?: UnassignedFilter },
): Promise<{ updated: number; skipped: number }> {
  const { data } = await api.post<{ updated: number; skipped: number }>(
    `/locations/${locationId}/assign-appointments`,
    input,
  );
  return data;
}

export async function getLocationStats(id: string): Promise<LocationStats> {
  const { data } = await api.get<LocationStats>(`/locations/${id}/stats`);
  return data;
}

export async function compareLocations(
  locationIds: string[],
  startDate?: string,
  endDate?: string,
): Promise<LocationComparison[]> {
  const { data } = await api.post<LocationComparison[]>('/locations/compare', {
    locationIds,
    startDate,
    endDate,
  });
  return data;
}

export async function assignProfessional(
  locationId: string,
  professionalId: string,
  isPrimary = false,
): Promise<void> {
  await api.post(`/locations/${locationId}/professionals`, { professionalId, isPrimary });
}

export async function removeProfessional(locationId: string, professionalId: string): Promise<void> {
  await api.delete(`/locations/${locationId}/professionals/${professionalId}`);
}

export async function assignPatient(
  locationId: string,
  patientId: string,
  isPreferred = false,
): Promise<void> {
  await api.post(`/locations/${locationId}/patients`, { patientId, isPreferred });
}

export async function removePatient(locationId: string, patientId: string): Promise<void> {
  await api.delete(`/locations/${locationId}/patients/${patientId}`);
}
